import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CountryFlag, PROVIDER_META, ProviderMark, formatRate, formatTokens, longContextRates, providerFor, ratesFor } from "./AronProviderMark";
import { modeLabel } from "./aronModeMeta";
import type { AronMessage, AronWorkMode } from "../types";

/** Self-contained CSS for the print window — no Tailwind, styles plain semantic
 * markup that react-markdown emits (headings, lists, tables, code). A4-friendly.
 *
 * Deliberately mirrors the app's own design tokens (index.css): zero radius, the
 * same red, the same greys, Inter + IBM Plex Mono. A printout that looks like a
 * different product makes the reader wonder whether it IS the same conversation. */
export const ARON_PRINT_CSS = `
  * { box-sizing: border-box; border-radius: 0; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
    color: #0a0a0a;
    font-size: 12px;
    line-height: 1.55;
    background: #fff;
  }
  .doc { max-width: 780px; margin: 0 auto; padding: 24px 20px 40px; }

  .doc-header { border-bottom: 2px solid #dc1f1f; padding-bottom: 12px; margin-bottom: 20px; }
  .doc-header .brand {
    font-family: "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #888;
  }
  .doc-header h1 { font-size: 18px; margin: 4px 0 10px; color: #0a0a0a; }
  .doc-header .meta {
    font-family: "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px; letter-spacing: .06em; text-transform: uppercase; color: #888; margin-top: 8px;
  }

  /* Model karty rozmowy — ten sam układ co kafel w pickerze trybu pracy. */
  .mode-strip { display: flex; align-items: center; gap: 10px; border: 1px solid #ccc; background: #fafafa; padding: 8px 10px; }
  .mode-tile { width: 32px; height: 32px; flex: none; border: 1px solid #ccc; background: #fff; display: flex; align-items: center; justify-content: center; }
  .mode-tile svg { display: block; }
  .mode-id { flex: 1 1 auto; min-width: 0; }
  .mode-id .label { display: block; font-size: 13px; font-weight: 600; }
  .mode-id .label .tier { font-family: "IBM Plex Mono", ui-monospace, monospace; font-size: 10px; font-weight: 400; color: #404040; border: 1px solid #ccc; padding: 0 3px; margin-left: 6px; }
  .mode-id .model { display: block; font-family: "IBM Plex Mono", ui-monospace, monospace; font-size: 11px; color: #404040; }
  .mode-vendor { display: flex; align-items: center; gap: 6px; font-family: "IBM Plex Mono", ui-monospace, monospace; font-size: 10px; letter-spacing: .06em; text-transform: uppercase; color: #888; }
  .mode-vendor svg { display: block; border: 1px solid rgba(0,0,0,.25); }
  .mode-rates { text-align: right; font-family: "IBM Plex Mono", ui-monospace, monospace; font-size: 11px; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .mode-rates .k { font-size: 9px; letter-spacing: .06em; color: #888; }
  .mode-rates .was { display: block; font-size: 9px; color: #888; }

  .msg { margin: 0 0 16px; page-break-inside: avoid; }
  .msg .who {
    font-family: "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 9px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: #888; margin-bottom: 5px;
  }

  /* Pytanie — jak dymek w rozmowie: przy prawej krawędzi, z czerwoną belką. */
  .msg.user { display: flex; flex-direction: column; align-items: flex-end; }
  .msg.user .bubble {
    max-width: 82%; white-space: pre-wrap; overflow-wrap: anywhere;
    background: #fdf0f0; border: 1px solid #f0c9c9; border-right: 3px solid #dc1f1f; padding: 8px 10px;
  }
  .msg.user .who { text-align: right; }

  /* Odpowiedź — awatar A2.0 po lewej, treść w kolumnie obok. */
  .msg.assistant { display: flex; gap: 8px; align-items: flex-start; }
  .avatar {
    width: 26px; height: 26px; flex: none; background: #dc1f1f; color: #fff;
    font-family: "IBM Plex Mono", ui-monospace, monospace; font-size: 9px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; margin-top: 2px;
  }
  .msg.assistant .body { min-width: 0; flex: 1 1 auto; }

  .md p { margin: 6px 0; }
  .md ul, .md ol { margin: 6px 0; padding-left: 22px; }
  .md li { margin: 2px 0; }
  .md h1, .md h2, .md h3 { margin: 12px 0 6px; line-height: 1.3; }
  .md h1 { font-size: 16px; } .md h2 { font-size: 14px; } .md h3 { font-size: 13px; }
  .md code { font-family: "IBM Plex Mono", ui-monospace, monospace; background: #e8e8e8; padding: 0 3px; font-size: 11px; }
  .md pre { background: #e8e8e8; padding: 8px 10px; overflow-x: auto; }
  .md pre code { background: none; padding: 0; }
  .md a { color: #dc1f1f; }
  .md .ref { background: #fdf0f0; color: #b81717; padding: 0 4px; font-weight: 500; }
  .md table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 11px; }
  .md th, .md td { border: 1px solid #ccc; padding: 4px 7px; text-align: left; vertical-align: top; }
  .md thead th { background: #f0f0f0; font-family: "IBM Plex Mono", ui-monospace, monospace; font-size: 10px; letter-spacing: .04em; text-transform: uppercase; }
  .md blockquote { border-left: 3px solid #ccc; margin: 6px 0; padding-left: 10px; color: #404040; }

  /* Ocena recenzenta — ten sam pasek co w rozmowie. */
  .gate { margin-top: 8px; border-left: 3px solid #047857; background: #f2f8f5; padding: 5px 8px; font-size: 11px; }
  .gate.warn { border-left-color: #d97706; background: #fdf7ec; }
  .gate .g-title { font-weight: 600; }
  .gate .g-num { font-variant-numeric: tabular-nums; color: #404040; }
  .gate .g-reason { color: #404040; margin-top: 2px; }

  .cost {
    font-family: "IBM Plex Mono", ui-monospace, monospace;
    font-size: 10px; color: #888; margin-top: 6px;
  }
  .foot {
    margin-top: 24px; padding-top: 8px; border-top: 1px solid #e0e0e0;
    font-family: "IBM Plex Mono", ui-monospace, monospace;
    font-size: 9px; letter-spacing: .08em; text-transform: uppercase; color: #888;
    display: flex; justify-content: space-between; gap: 12px;
  }
  @page { margin: 16mm 14mm; }
`;

function PrintMarkdown({ text }: { text: string }) {
  return (
    <div className="md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // Keep the custom aron-ref: scheme intact (default transform strips it).
        urlTransform={(url) => url}
        components={{
          a({ href, children }) {
            // Internal app references are dead links on paper — render as a plain chip.
            if (href?.startsWith("aron-ref:")) return <span className="ref">{children}</span>;
            return (
              <a href={href} target="_blank" rel="noreferrer">
                {children}
              </a>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function fmtTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtUsd(n: number): string {
  return `$${n >= 0.01 ? n.toFixed(2) : n.toFixed(4)}`;
}

function fmtCost(v?: string): string | null {
  const n = Number(v);
  if (!v || !Number.isFinite(n) || n <= 0) return null;
  return `koszt: ${fmtUsd(n)}`;
}

/** The gate verdict is appended to the answer text for export; on screen it has its
 * own block. Print does the same, so paper and screen say the same thing twice-free. */
function stripGateNote(text: string): string {
  const i = text.indexOf("🛡 **KONTROLER JAKOŚCI");
  return i === -1 ? text : text.slice(0, i).trimEnd();
}

/** Which model answered, whose it is, and what it costs — the same identity strip
 * the picker shows, so the printout can be read without the app. */
function ModeStrip({ mode, modelId }: { mode?: AronWorkMode; modelId?: string }) {
  const model = mode?.model || modelId || "";
  if (!model) return null;
  const provider = providerFor(model);
  const meta = provider ? PROVIDER_META[provider] : null;
  const rates = ratesFor(model);
  return (
    <div className="mode-strip">
      {provider && (
        <span className="mode-tile" style={meta ? { color: meta.color } : undefined}>
          <ProviderMark provider={provider} size={20} />
        </span>
      )}
      <span className="mode-id">
        <span className="label">
          {mode ? modeLabel(mode) : model}
          {mode?.cost && <span className="tier">{mode.cost}</span>}
        </span>
        <span className="model">
          {mode ? `${model}${mode.detail ? ` · ${mode.detail}` : ""}` : model}
        </span>
      </span>
      {meta && (
        <span className="mode-vendor">
          <CountryFlag country={meta.country} />
          {meta.name} · {meta.countryLabel}
        </span>
      )}
      {rates && (
        <span className="mode-rates">
          <span className="k">IN </span>
          {formatRate(rates.input)}
          <span className="k"> OUT </span>
          {formatRate(rates.output)}
          <span className="k"> /1M</span>
          {rates.wasInput !== undefined && rates.wasOutput !== undefined && (
            <span className="was">
              było <s>{`${formatRate(rates.wasInput)} / ${formatRate(rates.wasOutput)}`}</s>
            </span>
          )}
          {longContextRates(rates) && (
            <span className="was">
              {" "}&gt;{formatTokens(longContextRates(rates)!.threshold)} tokenów: {formatRate(longContextRates(rates)!.input)} / {formatRate(longContextRates(rates)!.output)}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/** Static, print-friendly rendering of an ARON conversation. Rendered to HTML with
 * renderToStaticMarkup and dropped into a fresh window for the browser print / Save-as-PDF. */
export function AronConversationPrint({
  title,
  subject,
  messages,
  generatedAt,
  mode,
  modelId,
  autopilot,
}: {
  title: string;
  /** Conversation name — the printout's actual subject. */
  subject?: string;
  messages: AronMessage[];
  generatedAt: string;
  mode?: AronWorkMode;
  modelId?: string;
  autopilot?: boolean;
}) {
  const visible = messages.filter(
    (m) => (m.role === "user" || m.role === "assistant") && (m.text || m.attachments?.length),
  );
  const total = visible.reduce((sum, m) => {
    const n = Number(m.cost_usd);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  // Model wątku da się przełączyć w trakcie. Gdy w rozmowie pracował więcej niż
  // jeden, nagłówek z jednym modelem byłby nieprawdą — wtedy KAŻDA odpowiedź
  // podpisuje się swoim modelem. Wątek na jednym modelu drukuje się jak dotąd.
  const uzyteModele = new Set(
    visible
      .filter((m) => m.role === "assistant" && m.model_id)
      .map((m) => m.model_id as string),
  );
  const mieszany = uzyteModele.size > 1;

  return (
    <div className="doc">
      <div className="doc-header">
        <div className="brand">{title}</div>
        <h1>{subject || "Rozmowa"}</h1>
        <ModeStrip mode={mode} modelId={modelId} />
        <div className="meta">
          Wygenerowano: {generatedAt}
          {autopilot !== undefined &&
            ` · zapisy: ${autopilot ? "YOLO (bez kart do zatwierdzenia)" : "za zgodą człowieka"}`}
          {total > 0 && ` · koszt rozmowy: ${fmtUsd(total)}`}
          {mieszany &&
            ` · model zmieniany w trakcie (${uzyteModele.size}) — każda odpowiedź podpisana własnym`}
        </div>
      </div>

      {visible.map((m) => {
        if (m.role === "user") {
          return (
            <div className="msg user" key={m.id}>
              <div className="who">
                Pytanie{m.created_at ? ` · ${fmtTs(m.created_at)}` : ""}
              </div>
              <div className="bubble">{m.text}</div>
              {m.attachments && m.attachments.length > 0 && (
                <div className="cost">
                  Załączniki: {m.attachments.map((a) => a.filename).join(", ")}
                </div>
              )}
            </div>
          );
        }

        const cost = fmtCost(m.cost_usd);
        const gate = m.gate;
        const gateOk =
          gate?.passed !== false &&
          (gate?.threshold == null || (gate?.score ?? 0) >= gate.threshold);
        return (
          <div className="msg assistant" key={m.id}>
            <div className="avatar">A2.0</div>
            <div className="body">
              <div className="who">
                ARON{m.created_at ? ` · ${fmtTs(m.created_at)}` : ""}
                {mieszany && m.model_id
                  ? ` · model: ${m.model_label || m.model_id}`
                  : ""}
              </div>
              {m.status === "error" ? (
                <div className="md">
                  <p>{m.error || "Wystąpił błąd."}</p>
                </div>
              ) : (
                <PrintMarkdown text={gate ? stripGateNote(m.text) : m.text} />
              )}
              {gate && gate.score != null && (
                <div className={`gate ${gateOk ? "" : "warn"}`}>
                  <span className="g-title">Recenzent </span>
                  <span className="g-num">
                    {gate.score}/100
                    {gate.threshold != null && ` · próg ${gate.threshold}`}
                    {gate.attempt != null && gate.attempt > 1 && ` · podejście ${gate.attempt}`}
                  </span>
                  {!gateOk && gate.reason && <div className="g-reason">{gate.reason}</div>}
                </div>
              )}
              {cost && <div className="cost">{cost}</div>}
            </div>
          </div>
        );
      })}

      <div className="foot">
        <span>ARON · GK ELEKTIM</span>
        <span>{generatedAt}</span>
      </div>
    </div>
  );
}
